"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Menu, X, ChevronDown } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "@/components/search/GlobalSearch";

import { TopBanner } from "@/components/ads/TopBanner";

const navigation = [
    { name: "Accueil", href: "/" },
    {
        name: "Découvrir",
        items: [
            { name: "Histoire", href: "/histoire" },
            { name: "Culture & Traditions", href: "/culture" },
            { name: "Carte Interactive", href: "/carte" },
            { name: "Galerie Nostalgie", href: "/nostalgie" },
            { name: "Guide des Nouveaux", href: "/bienvenue" },
        ]
    },
    {
        name: "Vie Pratique",
        items: [
            { name: "Annuaire Local", href: "/annuaire" },
            { name: "Santé", href: "/sante" },
            { name: "Éducation", href: "/education" },
            { name: "Transports", href: "/transport" },
            { name: "Immobilier", href: "/immobilier" },
            { name: "Emploi", href: "/emploi" },
        ]
    },
    { name: "Actualités", href: "/actualites" },
    { name: "Mairie", href: "/mairie" },
];

export function Header() {
    const [isOpen, setIsOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [scrolled, setScrolled] = useState(false);

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <header className={cn(
            "fixed top-0 z-50 w-full border-b transition-all duration-300",
            scrolled || isOpen ? "bg-background/95 backdrop-blur-md border-white/10 shadow-lg" : "bg-transparent border-transparent"
        )}>
            <TopBanner />
            <Container>
                <div className="flex h-20 items-center justify-between">
                    <div className="flex md:flex-1">
                        <Link href="/" className="-m-1.5 p-1.5 text-2xl font-bold font-serif tracking-tight flex items-center gap-2">
                            <div className="relative w-10 h-10 overflow-hidden rounded-full border border-white/10 shadow-lg">
                                <img
                                    src="/logo.png"
                                    alt="Daalù Sébi Logo"
                                    className="object-cover w-full h-full"
                                />
                            </div>
                            <div>
                                <span className="text-white">Daalù</span> <span className="text-primary">Sébi</span>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex md:gap-x-2 items-center">
                        {navigation.map((item) => (
                            <div key={item.name} className="relative group">
                                {item.items ? (
                                    <div
                                        className="relative"
                                        onMouseEnter={() => setActiveDropdown(item.name)}
                                        onMouseLeave={() => setActiveDropdown(null)}
                                    >
                                        <button className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-foreground hover:text-primary transition-colors">
                                            {item.name}
                                            <ChevronDown className="w-4 h-4" />
                                        </button>

                                        {/* Dropdown Menu */}
                                        <div className={cn(
                                            "absolute top-full left-0 w-56 pt-2 transition-all duration-200 origin-top-left",
                                            activeDropdown === item.name ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible"
                                        )}>
                                            <div className="bg-[#0f1012] border border-white/10 rounded-xl shadow-xl overflow-hidden p-2">
                                                {item.items.map((subItem) => (
                                                    <Link
                                                        key={subItem.name}
                                                        href={subItem.href}
                                                        className="block px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-primary rounded-lg transition-colors"
                                                    >
                                                        {subItem.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <Link
                                        href={item.href!}
                                        className="px-4 py-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
                                    >
                                        {item.name}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="hidden md:flex md:flex-1 md:justify-end items-center gap-4">
                        <GlobalSearch />
                        <Button variant="outline" size="sm" className="border-primary/50 text-primary hover:bg-primary hover:text-black transition-all">
                            Espace Citoyen
                        </Button>
                    </div>

                    {/* Mobile Menu Button + Search */}
                    <div className="flex items-center gap-4 md:hidden">
                        <GlobalSearch />
                        <button
                            type="button"
                            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-400 hover:text-white"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <span className="sr-only">Open main menu</span>
                            {isOpen ? (
                                <X className="h-6 w-6" aria-hidden="true" />
                            ) : (
                                <Menu className="h-6 w-6" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </Container>


            {/* Mobile Menu */}
            {isOpen && (
                <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-white/10 max-h-[calc(100vh-80px)] overflow-y-auto">
                    <div className="space-y-1 px-4 pb-6 pt-2">
                        {navigation.map((item) => (
                            <div key={item.name} className="py-2">
                                {item.items ? (
                                    <div className="space-y-2">
                                        <div className="text-sm font-bold text-gray-500 uppercase px-3 pt-2">{item.name}</div>
                                        {item.items.map((subItem) => (
                                            <Link
                                                key={subItem.name}
                                                href={subItem.href}
                                                className="block rounded-lg px-3 py-2 text-base font-medium text-foreground hover:bg-white/5 hover:text-primary pl-6"
                                                onClick={() => setIsOpen(false)}
                                            >
                                                {subItem.name}
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    <Link
                                        href={item.href!}
                                        className="block rounded-lg px-3 py-2 text-base font-bold text-foreground hover:bg-white/5 hover:text-primary"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {item.name}
                                    </Link>
                                )}
                            </div>
                        ))}
                        <div className="mt-6 pt-6 border-t border-white/10">
                            <Button className="w-full bg-primary text-black font-bold hover:bg-primary/90">
                                Connecter mon compte
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
